import { Form, Button, Alert, Drawer } from 'antd';
import { DrawerStyles } from 'antd/es/drawer/DrawerPanel';
import InputFormBox from 'components/bank/AGBBank/InputFormBox';
import NoComponent from 'components/bank/AGBBank/NoComponent';
import AccountDropdown from 'components/shared/AccountDropdown';
import FormItemInput from 'components/shared/FormItemInput';
import PartnerResponseModal from 'components/shared/PartnerResponseModal';
import TransferConfirmation from 'components/shared/TransferConfirmation';
import agbColors from 'constants/Colors/agbColors';
import { filteredFormInputItems } from 'constants/InputItems/agbInputItems';
import { useLanguage } from 'hooks/Language/useLanguage';
import { ITransferForm } from 'interfaces/components/pageProps';
import { LocationState } from 'interfaces/otherInterface/dynamicInput';
import { IConfirmationData } from 'interfaces/queries/transactions';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';

const CashWithdraw: React.FC<ITransferForm> = ({
  form,
  step,
  onPrevious,
  accountNumber,
  setAccountNumber,
  loadingAccount,
  selectAccountNumber,
  dynamicFormItem,
  isDrawerOpen,
  handleCloseOpenDrawer,
}) => {
  const { translate } = useLanguage();
  const location = useLocation();
  const state = location.state as LocationState;

  const drawerStyles: DrawerStyles = {
    header: {
      background: agbColors.navyBlueColor,
      color: agbColors.lightBlueColor,
    },
  };

  const {
    transferConfirmationData,
    onPINValuesChange,
    loadTransferConfirmation,
    onHandleCashWithdrawTransferConfirmation,
    onHandleCashBackRequest,
    loadCashBack,
    onHandleClosePartnerResponseModal,
    openPartnerResponseModal,
    partnerResponseData,
    handleBackClick,
  } = dynamicFormItem;

  const formItems = useMemo(
    () =>
      filteredFormInputItems(['amount']).map((item, index) => (
        <FormItemInput
          key={index}
          label={translate(item.label)}
          placeholder={translate(item.placeholder)}
          name={item.name}
          disabled={item.disabled}
          rules={[{ required: true }]}
          className="small-margin-bottom"
          suffix={selectAccountNumber?.[0]?.currency}
        />
      )),
    [],
  );

  const convertConfirmationData = (data: IConfirmationData) => {
    return [
      { label: 'Amount to be sent', value: data?.Amount },
      { label: 'Fees', value: data?.Fees },
      { label: 'Exchange fees', value: data?.ExchangeFees },
      { label: 'Exchange rate', value: data?.ExchangeRate },
      { label: 'Taxes', value: data?.Taxes },
      { label: 'Total amount', value: data?.TotalAmount },
    ];
  };

  const confirmationItems = convertConfirmationData(
    transferConfirmationData,
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <InputFormBox
            title={state?.chosenOption}
            select={[
              <Form
                layout="vertical"
                style={{ marginBottom: '-40px' }}
              >
                <Form.Item
                  label={translate('Select an Account')}
                  className="small-margin-bottom"
                >
                  <AccountDropdown
                    account={accountNumber}
                    loading={loadingAccount}
                    options={selectAccountNumber}
                    setAccount={setAccountNumber}
                    size="100%"
                  />
                </Form.Item>
              </Form>,
            ]}
            formItems={[
              <Form
                form={form}
                layout="vertical"
                className="small-margin-bottom"
                key="form"
                initialValues={form}
                onFinish={onHandleCashWithdrawTransferConfirmation}
              >
                <Form.Item
                  label=" "
                  className="large-margin-bottom small-margin-top"
                >
                  <Alert
                    message={translate('Withdraw using an agent')}
                    type="info"
                    showIcon
                  />
                </Form.Item>
                {formItems}
                <Form.Item className="custom-drawer-footer">
                  <div className="custom-flex">
                    <Button
                      type="primary"
                      loading={loadTransferConfirmation}
                      htmlType="submit"
                    >
                      {translate('Next')}
                    </Button>
                  </div>
                </Form.Item>
              </Form>,
            ]}
          />
        );
      case 2:
        return (
          <TransferConfirmation
            isLoading={loadCashBack}
            items={confirmationItems}
            onHandleBack={onPrevious}
            onHandleConfirm={onHandleCashBackRequest}
            onValuesChange={onPINValuesChange}
          />
        );
      default:
        return <NoComponent />;
    }
  };

  return (
    <>
      <Drawer
        width={500}
        open={isDrawerOpen?.open}
        title={translate(isDrawerOpen?.drawerTitle)}
        styles={drawerStyles}
        closeIcon={false}
        extra={
          <Button
            onClick={handleCloseOpenDrawer}
            icon={<CloseOutlined />}
            variant="filled"
            color="danger"
          />
        }
      >
        {renderStep()}
      </Drawer>
      <PartnerResponseModal
        open={openPartnerResponseModal}
        onClose={onHandleClosePartnerResponseModal}
        data={partnerResponseData}
        handleBackClick={handleBackClick}
      />
    </>
  );
};

export default CashWithdraw;
